import {
  AutoComplete,
  Badge,
  Button,
  Divider,
  Form,
  FormListOperation,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Select,
  Table,
  Typography,
  Upload,
  UploadProps,
} from "antd";
import { RcFile } from "antd/es/upload";
import { api } from "api";
import { ApiService } from "api/api.service";
import { T_Api_Success_Res } from "api/api.types";
import { IconDocx, IconPdf } from "assets/icons/onboarding";
import { AxiosRequestConfig, AxiosResponse } from "axios";
import { SingleQueryWrapper } from "components/QueryWrapper";
import OnboardingLayout from "layout/OnboardingLayout";
import { Fragment, ReactElement, useEffect, useState } from "react";
import {
  TbChevronLeft,
  TbChevronRight,
  TbExclamationCircleFilled,
  TbFileFilled,
  TbFileText,
  TbTrash,
} from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { APPLICATION_STATUS } from "types/application.types";
import { FREQUENCY_TYPES, LoanDto, LoanSchemaDto, OCR_STATUS } from "types/loan.types";
import { AvailableResources } from "types/permissionGroup.types";
import { LENDERS, MAX_FILE_UPLOAD_SIZE, WEEKLY_PAYMENTS_REDUCTION_MULTIPLIER } from "utils/constants";
import { formatMoney, getErrorMessage, humanFileSize, moneyFormatter, moneyParser, toTitleCase } from "utils/functions";
import { storedTokens } from "utils/storedTokens";
import ApplicationRedirect from "../components/ApplicationRedirect";
import LoanPieChart from "../components/LoanPieChart";
import styles from "../Onboarding.module.scss";
import { ONBOARDING_ROUTES } from "utils/routes";
import { queryClient } from "utils/queryClient";
import PageTitle from "components/PageTitle";
import DashboardLayout from "layout/DashboardLayout";
import { LoanFrequencyMultiplier } from "components/Application/constants";

const extensionIcons: Record<string, ReactElement> = {
  docx: <IconDocx />,
  pdf: <IconPdf />,
};

export function LoanDocsTable() {
  const navigate = useNavigate();
  const applicationId = storedTokens.selectedApplicationId.getToken();
  const allLoans = ApiService.onboarding.useGetArtifacts(applicationId, "loans");
  const bulkUpdateLoans = ApiService.loans.useBulkUpdate();
  const updateStatus = ApiService.onboarding.useUpdateStatus(applicationId);

  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [loanListForm] = Form.useForm<{ loans: LoanSchemaDto[] }>();
  const loanFormValue = Form.useWatch("loans", loanListForm);

  useEffect(() => {
    if (!allLoans?.data) {
      return;
    }
    const prevLoans = [...allLoans.data.result];

    let processingLoans = allLoans?.data?.result?.filter(
      (el) => el.ocrState === OCR_STATUS.PENDING || el.ocrState === OCR_STATUS.RUNNING,
    );

    if (processingLoans?.length === 0) return;
    const intervalId = setInterval(async () => {
      const newLoansRes = await api.get(`/application/${applicationId}/loans`);
      const newLoans = newLoansRes.data as T_Api_Success_Res<
        NonNullable<ReturnType<typeof ApiService.onboarding.useGetArtifacts<"loans">>["data"]>["result"]
      >;
      processingLoans.forEach((el) => {
        const curr = newLoans.result.find((i) => i._id === el._id);
        const keys: (keyof LoanSchemaDto)[] = [
          "creditor_name",
          "funded_amount",
          "payback_amount",
          "payments",
          "frequency",
          "ocrState",
        ];
        keys.forEach((key) => {
          loanListForm.setFieldValue(["loans", prevLoans.findIndex((i) => i._id === el._id), key], curr?.[key]);
        });
      });
      queryClient.setQueryData(["application", "artifact/all", "loans", applicationId], (prev: typeof newLoans) => {
        return {
          ...prev,
          result: prev.result.map((loan) => {
            const el = newLoans.result.find((i) => i._id === loan._id);
            if (el) return el;
            else return loan;
          }),
        };
      });
      processingLoans = newLoans.result.filter(
        (el) => el.ocrState === OCR_STATUS.PENDING || el.ocrState === OCR_STATUS.RUNNING,
      );
    }, 5000);
    if (processingLoans.length === 0) {
      clearInterval(intervalId);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [allLoans?.data]);

  if (!applicationId) {
    return <ApplicationRedirect />;
  }
  const currentDebt = loanFormValue?.reduce((prev, curr) => prev + (curr.due_amount ?? 0), 0);
  return (
    <DashboardLayout>
      <OnboardingLayout>
        <PageTitle pageTitle={[]} title="Upload Loan Docs - 2" />
        <div className="grid" style={{ gridTemplateColumns: "1fr 364px" }}>
          <div className="p-5">
            <SingleQueryWrapper
              showLoaderOn="isFetching"
              query={allLoans}
              renderData={(loans) => {
                return (
                  <Form
                    scrollToFirstError={{
                      behavior: (actions) =>
                        actions.forEach(({ el, top }) => {
                          el.scrollTop = top - 250;
                        }),
                    }}
                    form={loanListForm}
                    initialValues={{ loans: loans }}
                    onFinish={(values) => {
                      // if (
                      //   values.loans.some(
                      //     (el) => el.ocrState === OCR_STATUS.PENDING || el.ocrState === OCR_STATUS.RUNNING,
                      //   )
                      // ) {
                      //   return message.warning("Please wait till all loans are processed");
                      // }
                      // Todo: multiple update
                      const toUpdateLoans = values.loans.map((el) => {
                        if (el.payback_amount && el.payments) {
                          return { ...el, tenure: parseFloat(`${(el?.payback_amount / el?.payments).toFixed(2)}`) };
                        } else return el;
                      });
                      const totalWeeklyPayments = toUpdateLoans.reduce(
                        (prev, curr) =>
                          prev +
                          (curr.payments ?? 0) * (curr.frequency ? 5 / LoanFrequencyMultiplier[curr.frequency] : 0),
                        0,
                      );

                      bulkUpdateLoans.mutate(
                        { loans: toUpdateLoans },
                        {
                          onSuccess: () => {
                            updateStatus.mutate(
                              {
                                status: APPLICATION_STATUS.LOANS,
                                summary: {
                                  current_debt: currentDebt,
                                  weekly_payments: totalWeeklyPayments,
                                },
                              },
                              {
                                onSuccess: () => {
                                  navigate(ONBOARDING_ROUTES.UploadLoanDocs_3);
                                },
                              },
                            );
                          },
                        },
                      );
                    }}
                  >
                    <Form.List name={"loans"}>
                      {(fields, { add, remove }) => {
                        return (
                          <div className="bg-white border border-solid border-gray-300 rounded-3xl overflow-auto">
                            <div className="flex justify-between items-center px-6 py-4 bg-gray-100 border-[0] border-b border-solid border-gray-300 ">
                              <Typography.Title level={4} className="!m-0">
                                Here is your summary
                              </Typography.Title>
                              <div className="inline-flex gap-2">
                                <Button
                                  onClick={() => setIsFormModalOpen(true)}
                                  variant="filled"
                                  color="primary"
                                  className="border-purple-300"
                                  icon={<TbFileFilled />}
                                >
                                  Add Creditor Manually
                                </Button>
                                <ModalForm
                                  isFormModalOpen={isFormModalOpen}
                                  setIsFormModalOpen={setIsFormModalOpen}
                                  addFormItem={add}
                                />
                                {/* <Button
                                onClick={() => setIsUploadModalOpen(true)}
                                variant="solid"
                                color="primary"
                                icon={<TbFileFilled />}
                              >
                                Upload Another Loan
                              </Button> */}
                                <ModalUpload
                                  isUploadModalOpen={isUploadModalOpen}
                                  setIsUploadModalOpen={setIsUploadModalOpen}
                                  addFormItem={add}
                                  allLoansQuery={allLoans}
                                />
                              </div>
                            </div>
                            <div className="flex flex-col gap-4 p-6 ">
                              {fields.map((field) => {
                                const loan = loanListForm.getFieldValue(["loans", field.name]) as LoanSchemaDto;
                                const attachments = loan?.attachments;
                                const isLoanProcessing =
                                  loan.ocrState === OCR_STATUS.PENDING || loan.ocrState === OCR_STATUS.RUNNING;
                                return (
                                  <div className="flex flex-col" key={field.key}>
                                    <div
                                      className={`flex justify-between p-2 border border-b-0 border-solid border-gray-200 rounded-t-xl ${
                                        loan.ocrState === OCR_STATUS.PENDING || loan.ocrState === OCR_STATUS.RUNNING
                                          ? "gray_progress_loader"
                                          : ""
                                      }`}
                                    >
                                      <div className="inline-flex gap-2 items-center">
                                        <Form.Item
                                          name={[field.name, "creditor_name"]}
                                          style={{ width: 450 }}
                                          className="!m-0"
                                          rules={
                                            isLoanProcessing
                                              ? []
                                              : [{ required: true, message: `Creditor Name is required` }]
                                          }
                                        >
                                          <AutoComplete
                                            options={LENDERS}
                                            placeholder="Creditor Name"
                                            showSearch
                                            allowClear
                                            disabled={isLoanProcessing}
                                          />
                                        </Form.Item>
                                        {attachments?.length ? (
                                          <>
                                            <TbFileText className="stroke-gray-400" />
                                            <Typography.Text type="secondary" className="text-sm" ellipsis>
                                              {(attachments?.[0] as any)?.original_file_name}
                                            </Typography.Text>
                                          </>
                                        ) : null}
                                      </div>
                                      <div className="inline-flex gap-2 items-center">
                                        {/* <Button size="small" icon={<LuFileText />}>
                                          View
                                        </Button> */}
                                        {/* <Button size="small" icon={<LuPencil />}>
                                          Edit
                                        </Button> */}
                                        {!(
                                          loan.ocrState === OCR_STATUS.STOPPED || loan.ocrState === OCR_STATUS.MODIFIED
                                        ) ? (
                                          <Badge
                                            status={
                                              isLoanProcessing
                                                ? "processing"
                                                : loan.ocrState === OCR_STATUS.FAILED
                                                  ? "error"
                                                  : loan.ocrState === OCR_STATUS.UPSTREAM ||
                                                      loan.ocrState === OCR_STATUS.COMPLETED
                                                    ? "success"
                                                    : "default"
                                            }
                                            className="inline-flex items-center"
                                            text={
                                              <Typography.Text
                                                type="secondary"
                                                className="text-xs font-semibold whitespace-nowrap"
                                              >
                                                {(isLoanProcessing
                                                  ? "In Process"
                                                  : loan.ocrState === OCR_STATUS.FAILED
                                                    ? "Retrieval Failed"
                                                    : loan.ocrState === OCR_STATUS.UPSTREAM ||
                                                        loan.ocrState === OCR_STATUS.COMPLETED
                                                      ? "Captured"
                                                      : loan.ocrState
                                                )?.toUpperCase()}
                                              </Typography.Text>
                                            }
                                          />
                                        ) : null}
                                        <DeleteLoanButton
                                          loanId={loan?._id}
                                          remove={() => {
                                            remove(field.name);
                                          }}
                                          disabled={isLoanProcessing}
                                        />
                                      </div>
                                    </div>
                                    <Table<LoanSchemaDto>
                                      className={styles.app_table}
                                      rowKey={"_id"}
                                      style={{ borderRadius: 0 }}
                                      bordered={true}
                                      size="small"
                                      columns={[
                                        {
                                          dataIndex: "funded_amount",
                                          title: "Funded Amount",
                                        },
                                        {
                                          dataIndex: "payback_amount",
                                          title: "Payback Amount",
                                        },
                                        {
                                          dataIndex: "due_amount",
                                          title: "Balance Amount",
                                        },
                                        {
                                          dataIndex: "payments",
                                          title: `Payments`,
                                        },
                                        {
                                          dataIndex: "frequency",
                                          title: "Frequency",
                                          width: 150,
                                        },
                                        // {
                                        //   dataIndex: "tenure",
                                        //   title: "Tenure",
                                        // },
                                      ].map((el) => {
                                        return {
                                          ...el,
                                          className:
                                            (loan as any)[el.dataIndex] === 0 ? styles.table_column_failed : "",
                                          render: () => {
                                            let formControl = (
                                              <InputNumber<number>
                                                min={1}
                                                prefix={"$"}
                                                formatter={moneyFormatter}
                                                parser={moneyParser}
                                                disabled={
                                                  el.dataIndex === "tenure" ||
                                                  (isLoanProcessing ? el.dataIndex != "due_amount" : false)
                                                }
                                                className="!w-full"
                                              />
                                            );
                                            if (el.dataIndex === "frequency") {
                                              formControl = (
                                                <Select
                                                  options={Object.entries(FREQUENCY_TYPES).map(([key, val]) => {
                                                    return { label: toTitleCase(val), value: val.toLowerCase() };
                                                  })}
                                                  disabled={isLoanProcessing}
                                                />
                                              );
                                            }
                                            return (
                                              <Form.Item
                                                className="!m-0"
                                                key={`${field.key}_${el.dataIndex}`}
                                                name={[field.name, el.dataIndex]}
                                                rules={
                                                  el.dataIndex === "tenure"
                                                    ? // || isLoanProcessing
                                                      []
                                                    : [
                                                        { required: true, message: `${el.title} is required` },

                                                        () => ({
                                                          validator(_, value) {
                                                            if (value === 0) {
                                                              return Promise.reject(new Error(`Value can't be 0`));
                                                            }
                                                            return Promise.resolve();
                                                          },
                                                        }),
                                                      ]
                                                }
                                              >
                                                {formControl}
                                              </Form.Item>
                                            );
                                          },
                                        };
                                      })}
                                      dataSource={[loanListForm.getFieldValue("loans")[field.key]]}
                                      pagination={false}
                                    />
                                  </div>
                                );
                              })}

                              <div className="flex justify-between items-center gap-3 bg-gray-100 p-5 rounded-xl border border-solid border-gray-300">
                                <Typography.Title className="!m-0" type="secondary" level={4}>
                                  Current Debt
                                </Typography.Title>
                                <Typography.Title className="!font-bold !m-0" level={4}>
                                  {formatMoney(currentDebt ?? 0)}
                                </Typography.Title>
                              </div>
                            </div>
                          </div>
                        );
                      }}
                    </Form.List>
                  </Form>
                );
              }}
            />
          </div>

          <div
            style={{ height: "calc(100vh - 242px)" }}
            className="sticky shadow-xl right-0 top-[168px] bg-white border-[0] border-l border-solid border-gray-200 p-5 overflow-y-auto"
          >
            <Typography.Title level={5} className="!mb-1 !text-purple-800">
              Your Account Analysis
            </Typography.Title>
            <Typography.Paragraph type="secondary" className="!text-xs !leading-4">
              The data you upload is processed on the go to provide you with accurate understanding
            </Typography.Paragraph>

            {/* <Divider />
          <div className="h-[200px]">
            <LineChart legend={{ x: "Revenue Trends" }} data={revenueTrendsData} />
          </div> */}

            <LoanPieChart loanData={loanFormValue} currentDebt={currentDebt} />
            <Divider />
            <Typography.Title level={5} className="!m-0 !text-center">
              Weekly Payments can be reduced to
            </Typography.Title>
            <Typography.Title level={1} className="!m-0 !text-center !text-emerald-500">
              {currentDebt ? formatMoney(currentDebt * WEEKLY_PAYMENTS_REDUCTION_MULTIPLIER) : ""}
            </Typography.Title>
            <Typography.Title level={5} type="secondary" className="!m-0 !text-center">
              Up to 75% reduction in payments
            </Typography.Title>
            {/* <div className="flex justify-center my-4">
            <Button size="large" variant="solid" type="primary">
              Know How
            </Button>
          </div> */}
            <Typography.Paragraph className="!text-xs">
              This estimation is a preliminary estimation and is based off your uploaded statements, and shall only be
              viable on further analysis.
            </Typography.Paragraph>
          </div>
        </div>
        <div className="sticky left-0 right-0 bottom-0 flex justify-between bg-white py-3 px-5 border-t border-solid border-[0px] border-gray-300 ">
          <Button
            size="large"
            icon={<TbChevronLeft />}
            onClick={() => {
              navigate(ONBOARDING_ROUTES.UploadLoanDocs_1);
            }}
          >
            Back
          </Button>
          <Button
            size="large"
            type="primary"
            icon={<TbChevronRight />}
            iconPosition="end"
            className="w-fit"
            onClick={() => {
              loanListForm.submit();
            }}
            loading={bulkUpdateLoans.isPending || updateStatus.isPending}
          >
            Continue
          </Button>
        </div>
      </OnboardingLayout>
    </DashboardLayout>
  );
}

function ModalForm({
  isFormModalOpen,
  setIsFormModalOpen,
  addFormItem,
}: {
  isFormModalOpen: boolean;
  setIsFormModalOpen: (val: boolean) => void;
  addFormItem: FormListOperation["add"];
}) {
  const applicationId = storedTokens.selectedApplicationId.getToken();

  const [creditorForm] = Form.useForm<LoanDto>();
  const frequency = Form.useWatch("frequency", creditorForm);
  const createLoan = ApiService.loans.useCreate();

  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const props: UploadProps = {
    name: "files",
    accept: ".pdf",
    multiple: false,
    beforeUpload: (file) => {
      const isLtMaxSize = file.size / 1024 / 1024 < MAX_FILE_UPLOAD_SIZE;
      const isSameFile = fileList.find((el) => {
        return el.name === file.name && el.size === file.size;
      });
      if (!isLtMaxSize) {
        message.error(`File must smaller than ${MAX_FILE_UPLOAD_SIZE}MB!`);
      } else if (isSameFile) {
        message.error("File with same name and same size already exists. Please rename the file and re-upload");
      } else {
        setFileList([file]);
      }

      return Upload.LIST_IGNORE;
    },
  };

  if (!applicationId) {
    return <ApplicationRedirect />;
  }
  return (
    <Modal
      title="Add Loan"
      open={isFormModalOpen}
      onOk={() => creditorForm.submit()}
      okText="Submit"
      okButtonProps={{ loading: isLoading }}
      onCancel={() => setIsFormModalOpen(false)}
    >
      <Form<LoanDto>
        scrollToFirstError
        form={creditorForm}
        layout="vertical"
        requiredMark="optional"
        initialValues={{ frequency: FREQUENCY_TYPES.WEEKLY }}
        onFinish={async (values) => {
          setIsLoading(true);
          let attachments: LoanSchemaDto["attachments"] = [];
          if (fileList.length) {
            const formData = new FormData();
            // fileList.forEach((file) => {
            // });
            formData.append("files", fileList?.[0]);
            formData.append("category", AvailableResources.LOAN);

            const config: AxiosRequestConfig = {
              // handles the upload progress
              // onUploadProgress: (event) => {
              //   const percent = Math.floor((event.loaded / (event?.total ?? 1000)) * 100);
              //   setUploadPercent(percent);
              // },
            };
            const res: AxiosResponse<T_Api_Success_Res<{ attachments: string[] }>> | null = await api
              .post("/file/upload", formData, config)
              .catch((error) => {
                setIsLoading(false);
                message.error(getErrorMessage(error));
                return null;
              });
            if (!res) {
              return;
            }
            attachments = res.data?.result?.attachments;
          }
          createLoan.mutate(
            {
              ...values,
              payback_amount: values?.payback_amount ?? (values?.funded_amount ?? 0) * 1.49,
              tenure: Math.floor(values.payback_amount ?? 0 / (values?.payments ?? 0)),
              attachments,
              application_id: applicationId,
              ocrState: OCR_STATUS.COMPLETED,
            },
            {
              onSuccess: async (data) => {
                message.success("Loan Added Successfully");
                // await queryClient.invalidateQueries({ queryKey: ["application", "loan/all", applicationId] });
                // loanListForm.();
                addFormItem(data.result);
                setIsLoading(false);
                creditorForm.resetFields();
                setFileList([]);
                setIsFormModalOpen(false);
              },
              onError: () => {
                setIsLoading(false);
              },
            },
          );
        }}
      >
        <Form.Item<LoanDto> name="creditor_name" label="Creditor Name" rules={[{ required: true }]}>
          <AutoComplete options={LENDERS} showSearch allowClear />
        </Form.Item>
        <div className="grid grid-cols-12 gap-x-2">
          <div className="col-span-6">
            <Form.Item<LoanDto> name="funded_amount" label="Funded Amount" rules={[{ required: true }]}>
              <InputNumber<number>
                formatter={moneyFormatter}
                parser={moneyParser}
                prefix="$"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
          <div className="col-span-6">
            <Form.Item<LoanDto> name="payback_amount" label="Payback Amount" rules={[{ required: false }]}>
              <InputNumber<number>
                formatter={moneyFormatter}
                parser={moneyParser}
                prefix="$"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
          <div className="col-span-6">
            <Form.Item<LoanDto> name="due_amount" label="Balance Amount" rules={[{ required: true }]}>
              <InputNumber<number>
                formatter={moneyFormatter}
                parser={moneyParser}
                prefix="$"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
          <div className="col-span-6">
            <Form.Item<LoanDto>
              name="payments"
              label={`${toTitleCase(frequency)} Payment`}
              rules={[{ required: true }]}
            >
              <InputNumber<number>
                formatter={moneyFormatter}
                parser={moneyParser}
                prefix="$"
                style={{ width: "100%" }}
              />
            </Form.Item>
          </div>
          <div className="col-span-12">
            <Form.Item<LoanDto> name="frequency" label="Frequency" rules={[{ required: true }]}>
              <Select
                options={Object.entries(FREQUENCY_TYPES).map(([key, val]) => {
                  return { label: toTitleCase(val), value: val };
                })}
              />
            </Form.Item>
          </div>
        </div>
        <div className="flex gap-2 justify-between items-center">
          <div className="inline-flex gap-1 items-center">
            <Typography.Text>Loan Document</Typography.Text>
            <Typography.Text type="secondary" className="italic">
              (optional)
            </Typography.Text>
          </div>
          <Upload {...props}>
            <Button icon={<TbFileText />} variant="filled" color="primary" className="border-purple-300">
              Upload
            </Button>
          </Upload>
        </div>

        <div className="flex flex-col gap-3 mt-3 mb-6">
          {fileList.map((file) => {
            const x = file.name?.split(".");
            const extension = x[x?.length - 1];
            const fileName = file?.name;

            return (
              <div
                key={file.uid}
                className="bg-white rounded-xl border border-solid border-gray-300 flex items-center gap-3 p-3"
              >
                <div className="text-3xl">{extensionIcons[extension]}</div>
                <div className="flex gap-3 justify-between w-full">
                  <div className="">
                    <Typography.Title level={5} className="!mb-1">
                      {fileName}
                    </Typography.Title>
                    <Typography.Text type="secondary">{humanFileSize(file?.size ?? 0)}</Typography.Text>
                  </div>
                  {/* {progress?.[file?.uid] ? <Progress percent={progress?.[file?.uid]} /> : null} */}
                </div>
                <Button
                  variant="text"
                  color="danger"
                  danger
                  icon={<TbTrash />}
                  onClick={() => {
                    setFileList((prev) => {
                      return prev.filter((el) => el.uid !== file.uid);
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
      </Form>
    </Modal>
  );
}

function ModalUpload({
  isUploadModalOpen,
  setIsUploadModalOpen,
  addFormItem,
  allLoansQuery,
}: {
  isUploadModalOpen: boolean;
  setIsUploadModalOpen: (val: boolean) => void;
  addFormItem: FormListOperation["add"];
  allLoansQuery: ReturnType<typeof ApiService.onboarding.useGetArtifacts<"loans">>;
}) {
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const applicationId = storedTokens.selectedApplicationId.getToken();
  const commitLoan = ApiService.onboarding.useCommitArtifact("loans");

  const props: UploadProps = {
    name: "files",
    accept: ".pdf",
    multiple: true,
    beforeUpload: (file) => {
      const isLtMaxSize = file.size / 1024 / 1024 < MAX_FILE_UPLOAD_SIZE;
      const isSameFile = fileList.find((el) => {
        return el.name === file.name && el.size === file.size;
      });
      if (!isLtMaxSize) {
        message.error(`File must smaller than ${MAX_FILE_UPLOAD_SIZE}MB!`);
      } else if (isSameFile) {
        message.error("File with same name and same size already exists. Please rename the file and re-upload");
      } else {
        setFileList((prev) => [...prev, file]);
      }

      return Upload.LIST_IGNORE;
    },
  };
  if (!applicationId) {
    return <ApplicationRedirect />;
  }
  return (
    <Modal
      title="Upload New Loans"
      open={isUploadModalOpen}
      okButtonProps={{ loading: isLoading }}
      onOk={() => {
        setIsLoading(true);
        const formData = new FormData();
        fileList.forEach((file) => {
          formData.append("files", file);
        });
        formData.append("category", AvailableResources.LOAN);

        const config: AxiosRequestConfig = {
          // handles the upload progress
          // onUploadProgress: (event) => {
          //   const percent = Math.floor((event.loaded / (event?.total ?? 1000)) * 100);
          //   setUploadPercent(percent);
          // },
        };

        // post request to the api
        api
          .post("/file/upload", formData, config)
          .then((res: AxiosResponse<T_Api_Success_Res<{ attachments: string[] }>>) => {
            const resFile = res.data.result;
            // message.success("File upload successful");
            commitLoan.mutate(
              {
                applicationId: applicationId,
                body: {
                  attachments: resFile?.attachments,
                },
              },
              {
                onSuccess: async (data) => {
                  // Todo: check this with new flow
                  // const refetchedQuery = await allLoansQuery.refetch();
                  // refetchedQuery.data?.result
                  //   ?.filter((el) => data.result.summary.loans.includes(el._id)) // changed
                  //   ?.forEach((el) => {
                  //   });
                  data.result.summary.loans.forEach((loanId) => {
                    addFormItem({
                      _id: loanId,
                      ocrState: OCR_STATUS.PENDING,
                    });
                  });
                  setIsUploadModalOpen(false);
                  setFileList([]);
                },
                onError: () => {
                  setIsLoading(false);
                },
              },
            );
          })
          .catch((error) => {
            setIsLoading(false);
            message.error(getErrorMessage(error));
          });
      }}
      okText="Submit"
      onCancel={() => setIsUploadModalOpen(false)}
    >
      <div style={{ maxHeight: "calc(100vh - 150px)" }}>
        <Upload.Dragger className={styles.file_upload} showUploadList={false} {...props}>
          <div className="bg-white flex flex-col items-center justify-center">
            <img role="presentation" className="max-w-[400px]" src="/doc-upload.png" />
            <Typography.Title level={5}>Drag and drop loan documents to upload</Typography.Title>
            <Typography.Text type="secondary">
              Max size {MAX_FILE_UPLOAD_SIZE}MB. Supported file types .pdf
            </Typography.Text>
            <Button className="mt-4">Select Files</Button>
          </div>
        </Upload.Dragger>
        <div className="flex flex-col gap-3 my-6">
          {fileList.map((file) => {
            const x = file.name?.split(".");
            const extension = x[x?.length - 1];
            const fileName = file?.name;

            return (
              <div
                key={file.uid}
                className="bg-white rounded-xl border border-solid border-gray-300 flex items-center gap-3 p-3"
              >
                <div className="text-3xl">{extensionIcons[extension]}</div>
                <div className="flex gap-3 justify-between w-full">
                  <div className="">
                    <Typography.Title level={5} className="!mb-1">
                      {fileName}
                    </Typography.Title>
                    <Typography.Text type="secondary">{humanFileSize(file?.size ?? 0)}</Typography.Text>
                  </div>
                  {/* {progress?.[file?.uid] ? <Progress percent={progress?.[file?.uid]} /> : null} */}
                </div>
                <Button
                  variant="text"
                  color="danger"
                  danger
                  icon={<TbTrash />}
                  onClick={() => {
                    setFileList((prev) => {
                      return prev.filter((el) => el.uid !== file.uid);
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
}

function DeleteLoanButton({ loanId, remove, disabled }: { loanId: string; remove: () => void; disabled: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const deleteLoan = ApiService.loans.useDelete();

  return (
    <Popconfirm
      open={isOpen}
      title="Delete the loan"
      description="Once deleted, loan can't be recovered"
      onConfirm={() => {
        deleteLoan.mutate(loanId, {
          onSuccess: () => {
            remove();
            setIsOpen(false);
            message.success("Loan deleted");
          },
        });
      }}
      icon={<TbExclamationCircleFilled className="fill-red-500" />}
      okButtonProps={{
        danger: true,
        icon: <TbTrash />,
        loading: deleteLoan.isPending,
      }}
      onCancel={() => setIsOpen(false)}
      okText="Yes, Delete"
      cancelText="No"
    >
      <Button
        size="small"
        className="hover:!border-red-500 hover:!text-red-600"
        icon={<TbTrash className="stroke-red-500 flex-shrink-0" />}
        onClick={() => setIsOpen(true)}
        disabled={disabled}
      >
        Delete
      </Button>
    </Popconfirm>
  );
}
